'use client';

import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTranslation } from '@/hooks/useTranslation';
import { useRTL } from '@/hooks/useRTL';
import { cn } from '@/lib/utils';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { HelpCircle } from 'lucide-react';
import { motion } from 'framer-motion';

gsap.registerPlugin(ScrollTrigger);

const faqs = [
    { id: 'duration', questionKey: 'faq.questions.duration.question', answerKey: 'faq.questions.duration.answer' },
    { id: 'customization', questionKey: 'faq.questions.customization.question', answerKey: 'faq.questions.customization.answer' },
    { id: 'natural-teeth', questionKey: 'faq.questions.naturalTeeth.question', answerKey: 'faq.questions.naturalTeeth.answer' },
    { id: 'veneer-lifespan', questionKey: 'faq.questions.veneerLifespan.question', answerKey: 'faq.questions.veneerLifespan.answer' },
    { id: 'whitening-safety', questionKey: 'faq.questions.whiteningState.question', answerKey: 'faq.questions.whiteningState.answer' },
    { id: 'maintenance', questionKey: 'faq.questions.maintenance.question', answerKey: 'faq.questions.maintenance.answer' },
    { id: 'pain-management', questionKey: 'faq.questions.painManagement.question', answerKey: 'faq.questions.painManagement.answer' },
    { id: 'cost-factors', questionKey: 'faq.questions.costFactors.question', answerKey: 'faq.questions.costFactors.answer' },
    { id: 'suitable-candidates', questionKey: 'faq.questions.suitableCandidates.question', answerKey: 'faq.questions.suitableCandidates.answer' },
    { id: 'immediate-results', questionKey: 'faq.questions.immediateResults.question', answerKey: 'faq.questions.immediateResults.answer' }
];

export default function FAQSection() {
    const { t } = useTranslation('hollywood-smile');
    const { isRTL } = useRTL();
    const sectionRef = useRef<HTMLElement>(null);
    const titleRef = useRef<HTMLHeadingElement>(null);
    const accordionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const ctx = gsap.context(() => {
            // Title animation
            gsap.fromTo(titleRef.current,
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: titleRef.current,
                        start: "top 80%",
                        end: "bottom 20%",
                        once: true
                    }
                }
            );

            // Accordion animation
            gsap.fromTo(accordionRef.current,
                { opacity: 0, y: 30 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 0.8,
                    delay: 0.3,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: accordionRef.current,
                        start: "top 80%",
                        end: "bottom 20%",
                        once: true
                    }
                }
            );

        }, sectionRef);

        return () => ctx.revert();
    }, []);

    return (
        <section
            id="faq"
            ref={sectionRef}
            className="py-20 bg-white dark:bg-slate-800 relative overflow-hidden"
        >
            {/* Background decoration */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-20 left-10 w-64 h-64 bg-gold rounded-full blur-3xl"></div>
                <div className="absolute bottom-20 right-10 w-80 h-80 bg-gold rounded-full blur-2xl"></div>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[600px] h-[300px] bg-gold/10 rounded-full blur-3xl"></div>
            </div>

            <div className="container relative">
                {/* Section Header */}
                <div className="text-center max-w-4xl mx-auto mb-16">
                    <h2
                        ref={titleRef}
                        className="text-4xl md:text-5xl lg:text-6xl font-bold text-navy dark:text-white mb-8 font-serif"
                    >
                        {t('faq.title.part1')}{' '}
                        <span className="text-gold relative">
                            {t('faq.title.part2')}
                            <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gold rounded-full"></div>
                        </span>
                    </h2>
                    <p className="text-xl text-slate-600 dark:text-slate-300 leading-relaxed">
                        {t('faq.subtitle')}
                    </p>
                </div>

                {/* FAQ Accordion */}
                <div ref={accordionRef} className="max-w-4xl mx-auto">
                    <Accordion type="single" collapsible className="space-y-4">
                        {faqs.map((faq, index) => (
                            <motion.div
                                key={faq.id}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.1 }}
                                viewport={{ once: true }}
                            >
                                <AccordionItem
                                    value={faq.id}
                                    className="bg-slate-50 dark:bg-navy-950 border-slate-200 dark:border-slate-700 rounded-2xl px-6 py-2 data-[state=open]:border-gold/50 transition-all duration-300 group"
                                >
                                    <AccordionTrigger className="text-left hover:no-underline py-6 group-data-[state=open]:text-gold dark:group-data-[state=open]:text-gold transition-colors duration-300">
                                        <div className={cn(
                                            "flex items-start",
                                            isRTL ? "space-x-reverse space-x-4 text-right" : "space-x-4 text-left"
                                        )}>
                                            <div className="w-8 h-8 bg-gold rounded-full flex items-center justify-center shrink-0 mt-1 group-data-[state=open]:scale-110 transition-transform duration-300">
                                                <HelpCircle className="w-4 h-4 text-white" />
                                            </div>
                                            <span className="text-lg md:text-xl font-bold text-navy dark:text-white leading-relaxed">
                                                {t(faq.questionKey)}
                                            </span>
                                        </div>
                                    </AccordionTrigger>
                                    <AccordionContent className="text-slate-600 dark:text-slate-300 text-lg leading-relaxed pl-12 pb-6">
                                        <motion.div
                                            initial={{ opacity: 0 }}
                                            animate={{ opacity: 1 }}
                                            transition={{ duration: 0.3, delay: 0.1 }}
                                        >
                                            {t(faq.answerKey)}
                                        </motion.div>
                                    </AccordionContent>
                                </AccordionItem>
                            </motion.div>
                        ))}
                    </Accordion>
                </div>
            </div>
        </section>
    );
} 
